/** Prévia da atualização em massa: só troca valores em coordenadas inequívocas. */
export type LinhaDoArquivo = {
  centro_custo_codigo: string | null;
  categoria_id: string | null;
  categoria_nome: string | null;
  competencia: string | null;
  tipo: string;
  valor_centavos: string;
  subcategorias?: { nome: string; valor_centavos: string }[];
};

export type LinhaDoOrcamento = {
  linha_id: string;
  centro_custo_id: string;
  centro_custo_codigo: string;
  categoria_id: string;
  competencia: string;
  valor_centavos: string;
  subcategorias: { id: string; nome: string; valor_centavos: string }[];
};

export type AlteracaoEmMassa = {
  acao: "valor" | "subcategoria";
  linha_id: string | null;
  despesa_id?: string | null;
  centro_custo_id: string;
  categoria_id: string;
  competencia: string;
  tipo: string;
  nome?: string;
  valor_centavos: string;
};

export type PreviaEmMassa = {
  rotulo: string;
  antes: string;
  depois: string;
  nova: boolean;
};

const chave = (cc: string, cat: string, comp: string) => `${cc}|${cat}|${comp}`;
const nome = (s: string) => s.trim().toLocaleLowerCase("pt-BR");

export function planejarAtualizacaoEmMassa(
  arquivo: LinhaDoArquivo[],
  existentes: LinhaDoOrcamento[],
  centrosPorCodigo: Map<string, string>,
): { alteracoes: AlteracaoEmMassa[]; previa: PreviaEmMassa[]; conflitos: string[] } {
  const porCoordenada = new Map<string, LinhaDoOrcamento[]>();
  for (const l of existentes) {
    const k = chave(l.centro_custo_codigo, l.categoria_id, l.competencia);
    porCoordenada.set(k, [...(porCoordenada.get(k) ?? []), l]);
  }

  const alteracoes: AlteracaoEmMassa[] = [];
  const previa: PreviaEmMassa[] = [];
  const conflitos: string[] = [];
  for (const l of arquivo) {
    const ccCodigo = l.centro_custo_codigo;
    const ccId = ccCodigo ? centrosPorCodigo.get(ccCodigo) : undefined;
    if (!ccCodigo || !ccId || !l.categoria_id || !l.competencia) {
      conflitos.push("Uma linha do arquivo está sem centro de custo, categoria ou competência válida.");
      continue;
    }
    const rotulo = `${ccCodigo} · ${l.categoria_nome ?? l.categoria_id} · ${l.competencia.slice(0, 7)}`;
    const atuais = porCoordenada.get(chave(ccCodigo, l.categoria_id, l.competencia)) ?? [];
    if (atuais.length > 1) {
      conflitos.push(`${rotulo}: há mais de uma linha orçada; ajuste essa combinação na Planilha.`);
      continue;
    }
    const atual = atuais[0];
    const subsArquivo = l.subcategorias ?? [];
    const subsAtuais = atual?.subcategorias ?? [];
    if (atual && (subsArquivo.length > 0) !== (subsAtuais.length > 0)) {
      conflitos.push(`${rotulo}: o arquivo e o orçamento têm estruturas diferentes de subcategorias.`);
      continue;
    }
    const base = {
      linha_id: atual?.linha_id ?? null,
      centro_custo_id: atual?.centro_custo_id ?? ccId,
      categoria_id: l.categoria_id,
      competencia: l.competencia,
      tipo: l.tipo,
    };
    const locais = new Map(subsAtuais.map((s) => [nome(s.nome), s]));
    const nomesArquivo = new Set(subsArquivo.map((s) => nome(s.nome)));
    if (atual && subsArquivo.length > 0 && (
      locais.size !== subsAtuais.length || nomesArquivo.size !== subsArquivo.length
      || locais.size !== nomesArquivo.size || [...nomesArquivo].some((n) => !locais.has(n))
    )) {
      conflitos.push(`${rotulo}: as subcategorias não são as mesmas; ajuste essa combinação na Planilha.`);
      continue;
    }

    const mudancas: AlteracaoEmMassa[] = subsArquivo.length > 0
      ? subsArquivo.filter((s) => !atual || locais.get(nome(s.nome))?.valor_centavos !== s.valor_centavos)
        .map((s) => ({ ...base, acao: "subcategoria" as const,
          despesa_id: locais.get(nome(s.nome))?.id ?? null,
          nome: s.nome, valor_centavos: s.valor_centavos }))
      : !atual || atual.valor_centavos !== l.valor_centavos
        ? [{ ...base, acao: "valor", valor_centavos: l.valor_centavos }]
        : [];
    if (mudancas.length === 0) continue;
    alteracoes.push(...mudancas);
    previa.push({ rotulo, antes: atual?.valor_centavos ?? "0", depois: l.valor_centavos, nova: !atual });
  }
  return { alteracoes, previa, conflitos };
}
